"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";
import { X } from "lucide-react";

export interface SidePaneProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: string;
  side?: "left" | "right";
  className?: string;
}

export function SidePane({
  open,
  onClose,
  title,
  children,
  width = "max-w-md",
  side = "left",
  className,
}: SidePaneProps) {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0"
        style={{ zIndex: Z_INDEX.DRAWER_BACKDROP }}
        onClick={onClose}
        aria-hidden
      />

      {/* Pane */}
      <aside
        role="dialog"
        aria-modal="true"
        className={cn(
          "fixed inset-y-0 flex w-full flex-col bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.06),0_18px_48px_-14px_rgba(15,23,42,0.18)] dark:bg-zinc-900",
          width,
          side === "left" ? "left-0 rounded-r-2xl" : "right-0 rounded-l-2xl",
          "motion-safe:animate-in motion-safe:duration-300",
          side === "left"
            ? "motion-safe:slide-in-from-left"
            : "motion-safe:slide-in-from-right",
          className
        )}
        style={{ zIndex: Z_INDEX.DRAWER }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4 dark:border-zinc-800">
          {title && (
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {title}
            </h2>
          )}
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </aside>
    </>
  );
}
